import { useTheme } from "@/hooks/useTheme";

const industries = [
  {
    icon: "ri-heart-pulse-line",
    title: "Home Healthcare\n& Elder Care",
    description: "Manage patient intake, scheduling, and family communication seamlessly. Ensure no inquiry goes unanswered.",
    image: "https://public.readdy.ai/ai/img_res/cc53ab3a137d42744246f6fb96b4dbac.jpg",
    tag: "Healthcare",
  },
  {
    icon: "ri-tools-line",
    title: "Home Services",
    description: "Capture every call while crews are on-site. Automate scheduling, follow-ups, and job confirmations.",
    image: "https://readdy.ai/api/search-image?query=professional%20home%20services%20technician%20clean%20modern%20residential%20property%20bright%20natural%20light%20minimal%20background%20tools%20equipment%20worker&width=600&height=400&seq=ind002&orientation=landscape",
    tag: "Field Services",
  },
  {
    icon: "ri-flag-line",
    title: "Golf Courses",
    description: "Optimize tee time bookings, member communication, and event coordination. Increase utilization effortlessly.",
    image: "https://readdy.ai/api/search-image?query=luxury%20golf%20course%20green%20fairway%20morning%20light%20aerial%20view%20pristine%20manicured%20grass%20minimal%20clean%20sky%20background%20premium%20club&width=600&height=400&seq=ind003&orientation=landscape",
    tag: "Recreation",
  },
  {
    icon: "ri-restaurant-line",
    title: "Restaurants\n& Hospitality",
    description: "Streamline reservations, reduce no-shows, and automate guest follow-ups. Improve retention effortlessly.",
    image: "https://readdy.ai/api/search-image?query=upscale%20modern%20restaurant%20dining%20room%20elegant%20minimal%20interior%20warm%20ambient%20lighting%20clean%20table%20settings%20contemporary%20design&width=600&height=400&seq=ind004&orientation=landscape",
    tag: "Hospitality",
  },
  {
    icon: "ri-scales-3-line",
    title: "Legal Services",
    description: "Accelerate client intake, qualification, and follow-up. Eliminate admin bottlenecks and improve conversion.",
    image: "https://readdy.ai/api/search-image?query=modern%20law%20office%20clean%20minimal%20desk%20legal%20books%20bright%20natural%20light%20sophisticated%20workspace%20attorney%20professional%20interior%20design&width=600&height=400&seq=ind005&orientation=landscape",
    tag: "Legal",
  },
  {
    icon: "ri-line-chart-line",
    title: "Financial Services",
    description: "Improve response times, manage client journeys, and unify communication. Keep every opportunity moving.",
    image: "https://readdy.ai/api/search-image?query=modern%20financial%20advisory%20office%20clean%20professional%20workspace%20dual%20monitors%20data%20visualization%20minimal%20bright%20corporate%20interior%20design&width=600&height=400&seq=ind006&orientation=landscape",
    tag: "Finance",
  },
];

export default function IndustriesSection() {
  const { isDark } = useTheme();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const bg = isDark ? "#1C1A22" : "#FBF6EE";
  const headlineColor = isDark ? "white" : "#1C1A22";
  const mutedColor = isDark ? "rgba(255,255,255,0.38)" : "rgba(28,26,34,0.45)";
  const cardBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.7)";
  const cardBorder = isDark ? "rgba(255,255,255,0.07)" : "rgba(28,26,34,0.1)";
  const cardHoverBorder = isDark ? "rgba(232,118,26,0.25)" : "rgba(232,118,26,0.4)";
  const gradientFrom = isDark ? "#1C1A22" : "#FBF6EE";
  const tagBg = isDark ? "rgba(232,118,26,0.18)" : "rgba(232,118,26,0.12)";
  const tagBorder = isDark ? "rgba(232,118,26,0.3)" : "rgba(232,118,26,0.35)";
  const iconBg = isDark ? "rgba(232,118,26,0.12)" : "rgba(232,118,26,0.1)";

  return (
    <section id="industries" className="py-28 md:py-36 transition-colors duration-500" style={{ background: bg }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-20">
          <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: "#E8761A" }}>Built For</p>
          <h2 className="text-4xl md:text-6xl font-thin leading-tight mb-6 tracking-tight transition-colors duration-500" style={{ color: headlineColor }}>
            Operations That Can&apos;t Afford
            <br />
            <span className="font-semibold">to Miss Opportunities.</span>
          </h2>
          <p className="text-base md:text-lg max-w-xl mx-auto font-light leading-relaxed transition-colors duration-500" style={{ color: mutedColor }}>
            Every missed interaction equals lost revenue. We fix that.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {industries.map((ind) => (
            <div
              key={ind.title}
              className="group relative rounded-2xl overflow-hidden cursor-default transition-all duration-500"
              style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.borderColor = cardHoverBorder)}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.borderColor = cardBorder)}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={ind.image}
                  alt={ind.title.replace("\n", " ")}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(to top, ${gradientFrom} 0%, ${gradientFrom}66 50%, transparent 100%)` }}
                />
                <div
                  className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-medium text-white"
                  style={{ background: tagBg, border: `1px solid ${tagBorder}` }}
                >
                  {ind.tag}
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: iconBg }}>
                    <i className={`${ind.icon} text-sm`} style={{ color: "#E8761A" }}></i>
                  </div>
                  <h3 className="font-semibold text-base leading-tight transition-colors duration-500" style={{ color: headlineColor }}>
                    {ind.title.split("\n").map((line, i) => (
                      <span key={i}>{line}{i < ind.title.split("\n").length - 1 ? " " : ""}</span>
                    ))}
                  </h3>
                </div>
                <p className="text-sm leading-relaxed font-light transition-colors duration-500" style={{ color: mutedColor }}>{ind.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 text-center">
          <p className="text-sm mb-2 font-light transition-colors duration-500" style={{ color: mutedColor }}>No matter the industry, the goal is the same:</p>
          <p className="text-xl font-light mb-8 tracking-tight transition-colors duration-500" style={{ color: headlineColor }}>
            Connect systems. Eliminate gaps. <span className="font-semibold">Maximize outcomes.</span>
          </p>
          <button
            onClick={() => scrollTo("book-audit")}
            className="px-8 py-3.5 rounded-full text-[14px] font-light transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            style={{ border: "1px solid rgba(232,118,26,0.35)", color: "#E8761A", background: "transparent" }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = "rgba(232,118,26,0.08)")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = "transparent")}
          >
            Book Your System Audit
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}
